function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { useEffect } from 'react';
import styled from 'styled-components';
import { DateTime } from 'luxon';
import Calendar from 'react-calendar';
import Icon from '../../icon';
import { focusShadow, white, gray100, gray200, gray500, gray600, gray700, gray800, teal50, primary500 } from '../../theme/colors';
import { size4, size8, size12, size14, size16, size32 } from '../../theme/scale';
import { fontSemiBold } from '../../theme/fonts';
import { ELEVATION_MAP } from '../../theme/elevations';
/**
 * Returns a DateTime object given a JS Date object in browser's timezone
 */
const convertValueToDateTime = date => {
  return DateTime.fromJSDate(date, {
    zone: Intl.DateTimeFormat().resolvedOptions().timeZone
  });
};

/**
 * Determines if the given date and dateToCompare are the same calendar day
 * time in dateToCompare will be ignored
 */
export const isSameDay = (date, dateToCompare) => {
  const selectedDateTime = convertValueToDateTime(dateToCompare);
  return date.get('year') === selectedDateTime.get('year') && date.get('month') === selectedDateTime.get('month') && date.get('day') === selectedDateTime.get('day');
};

/**
 * Determines the class of a selected date, or null
 */
const selectedDateClass = (selectRange, selectedDate, targetDate) => {
  if (selectRange && selectedDate instanceof Array) {
    if (selectedDate[0] && isSameDay(targetDate, selectedDate[0]) || selectedDate[1] && isSameDay(targetDate, selectedDate[1])) {
      return 'selected-date';
    }
    if (targetDate.toJSDate() > selectedDate[0] && targetDate.toJSDate() < selectedDate[1]) {
      return 'range-date';
    }
  } else {
    if (isSameDay(targetDate, selectedDate)) {
      return 'selected-date';
    }
  }
  return null;
};
/**
 * Determines the class of a non-selected date
 */
const nonSelectedDateClass = (highlightedDates, targetDate, minDate, maxDate, today) => {
  const hasHighlightedDate = highlightedDates.some(highlightedDate => targetDate.hasSame(DateTime.fromJSDate(highlightedDate), 'day'));
  if (hasHighlightedDate) {
    return 'highlighted-date';
  }
  if (targetDate && (minDate != null && targetDate.toJSDate() < minDate || maxDate != null && targetDate.toJSDate() > maxDate)) {
    return 'out-of-range';
  }
  if (today && targetDate && targetDate.toJSDate() < today) {
    return 'past-date';
  }
};
export const mapDateToClass = (date, today, selectedDate, highlightedDates = [], selectRange = false, minDate, maxDate) => {
  const targetDate = date ? DateTime.fromJSDate(date, {
    zone: Intl.DateTimeFormat().resolvedOptions().timeZone
  }).startOf('day') : null;
  if (!targetDate) return;
  return (selectedDate && selectedDateClass(selectRange, selectedDate, targetDate)) ?? nonSelectedDateClass(highlightedDates, targetDate, minDate, maxDate, today);
};
const Wrapper = styled.div`
  border-radius: ${size4};
  box-shadow: ${({
  elevation
}) => ELEVATION_MAP[elevation]};
  overflow: hidden;
  width: 275px;

  button {
    background: transparent;
    border: 0;
    cursor: pointer;
    font-family: inherit;
    margin: 0;
    padding: 0;

    &:focus {
      box-shadow: 0 0 0 3px ${focusShadow};
      outline: none;
    }
  }

  .react-calendar {
    background: ${white};

    div {
      display: unset;
      flex-direction: row;
    }

    &__navigation {
      align-items: center;
      border-bottom: 1px solid ${gray200};
      display: flex !important;
      padding: ${size8} ${size16};
    }

    &__navigation__label {
      color: ${gray800};
      font-family: ${fontSemiBold};
      font-size: ${size14};
      order: -1;
      text-align: left;
    }

    &__navigation__arrow {
      align-items: center;
      color: ${gray600};
      display: flex;
      flex-direction: row;
    }

    &__viewContainer {
      padding: ${size16};
    }

    &__month-view__weekdays {
      margin-bottom: ${size4};
    }

    &__month-view__weekdays__weekday {
      color: ${gray800};
      font-family: ${fontSemiBold};
      font-size: ${size14};
      text-align: center;

      > abbr {
        text-decoration: none;
      }
    }

    &__month-view__days__day--neighboringMonth {
      color: ${({
  maxDate
}) => maxDate ? null : `${gray500} !important`};
    }

    &__tile {
      border: 1px solid transparent;
      color: ${gray700};
      flex: none;
      height: ${size32};

      &:hover {
        background: ${gray100};
      }

      &--now {
        border-color: ${primary500};
        color: ${primary500};
      }

      &.out-of-range {
        color: ${gray500};
      }

      &.past-date {
        color: ${gray600};
      }

      &.highlighted-date {
        background: ${teal50};
        color: ${gray700};
      }

      &.selected-date {
        background: ${primary500};
        color: ${white} !important;
      }

      &.range-date {
        background: ${gray200};
      }
    }
  }
`;
const Footer = styled.div`
  align-items: center;
  background: ${white};
  border-top: 1px solid ${gray200};
  color: ${gray700};
  display: flex;
  flex-direction: row;
  font-size: ${size12};
  padding: ${size14} ${size16};
`;
const LegendMarker = styled.div`
  background: ${teal50};
  border-radius: 50%;
  height: ${size14};
  margin-right: ${size8};
  width: ${size14};
`;
const DAY_ABBREVIATIONS = {
  en: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
  es: ['DO', 'L', 'M', 'MI', 'J', 'V', 'S']
};
export const MiniCalendar = ({
  value,
  onChange,
  highlightedDates = [],
  highlightedDatesLabel,
  minDate,
  maxDate,
  selectRange = false,
  elevation = '5',
  locale = 'en',
  closeCalendarMenu,
  ...props
}) => {
  const today = DateTime.local().startOf('day').toJSDate();
  const sanitizeDate = val => {
    if (val instanceof Array) {
      val[1] = DateTime.fromJSDate(val[1], {
        zone: Intl.DateTimeFormat().resolvedOptions().timeZone
      }).set({
        hour: 0,
        minute: 0,
        second: 0,
        millisecond: 0
      }).toJSDate();
    }
    onChange(val);
  };
  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  });
  const handleKeyDown = event => {
    if (event.key === 'Tab') {
      closeCalendarMenu?.();
    }
  };
  return /*#__PURE__*/React.createElement(Wrapper, _extends({
    elevation: elevation,
    maxDate: Boolean(maxDate)
  }, props), /*#__PURE__*/React.createElement(Calendar, {
    onChange: sanitizeDate,
    value: value,
    calendarType: "US",
    prevLabel: /*#__PURE__*/React.createElement(Icon, {
      type: "ChevronLeft"
    }),
    nextLabel: /*#__PURE__*/React.createElement(Icon, {
      type: "ChevronRight"
    }),
    prev2Label: null,
    next2Label: null,
    showFixedNumberOfWeeks: true,
    selectRange: selectRange,
    minDate: minDate,
    maxDate: maxDate,
    locale: locale,
    formatShortWeekday: (locale, date) => DAY_ABBREVIATIONS[locale][date.getDay()],
    tileClassName: ({
      date
    }) => mapDateToClass(date, today, value, highlightedDates, selectRange, minDate, maxDate)
  }), highlightedDatesLabel && /*#__PURE__*/React.createElement(Footer, null, /*#__PURE__*/React.createElement(LegendMarker, null), " ", highlightedDatesLabel));
};
export default MiniCalendar;
//# sourceMappingURL=index.js.map